import { useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import { AlertTriangleIcon, FileIcon, FileUpIcon, InfoIcon, Loader2, XIcon } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { type FileRejection, useDropzone } from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { useIndividualsServiceGetV1IndividualsByIndividualIdKey } from '@/api/client/queries/common'
import { discardOnUnload } from '@/api/discard-on-unload'
import { useCommitDocument } from '@/api/hooks/useCommitDocument'
import { useCreateDocuments } from '@/api/hooks/useCreateDocuments'
import { useDeleteDocument } from '@/api/hooks/useDeleteDocument'
import { useDiscardDrafts } from '@/api/hooks/useDiscardDrafts'
import { useDraftDocumentsLiveSync } from '@/api/hooks/useDraftDocumentsLiveSync'
import { DropzoneOverlay } from '@/components/molecules/dropzone-overlay'
import { FileTypeIcon } from '@/components/molecules/file-type-icon'
import { LeaveDraftsGuard } from '@/components/molecules/leave-drafts-guard'
import PageHeader from '@/components/molecules/page-header'
import { PlayVideoButton } from '@/components/molecules/play-video-button'
import { PreCheckConfirmDialog } from '@/components/organisms/pre-check-confirm-dialog'
import { PreCheckWarningInfoDialog } from '@/components/organisms/pre-check-warning-info-dialog'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { appConfig } from '@/config/app.config'
import { UPLOAD_DOCUMENTS_VIDEO } from '@/config/videos'
import { useSubscription } from '@/contexts/subscription-context'
import { usePreviewDocument } from '@/hooks/useDocumentPreview'
import { useIndividuals } from '@/hooks/useIndividuals'
import { useStringSet } from '@/hooks/useStringSet'
import { uploadToStoragePath } from '@/integrations/firebase'
import { useAppDispatch } from '@/redux/hooks'
import { AssistantActions } from '@/redux/reducers/assistant'

type DocumentFile = {
  id?: string
  bytes: number
  filePath: string
  mimeType: string
  name: string
  order: number
  originalName: string
  uploaded: boolean
}

type DraftDocument = {
  id: string
  preChecks?: {
    results?: { warnings: ('missing_person' | 'missing_pages')[] } | null
    status?: 'pending' | 'passed' | 'warning'
  }
  files: DocumentFile[]
  name: string
  status: string
}

type Props = {
  /**
   * Set when the page is mounted at `/errands/$id/upload`. Threads the errand link through
   * document creation and routes back to the errand instead of /documents once done. Taken as a
   * prop rather than read off a route so the same page can serve both mount points.
   */
  errandId?: string
}

export const DocumentUploadPage = ({ errandId }: Props) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const dispatch = useAppDispatch()
  const { selectedIndividual, selectedIndividualId } = useIndividuals()
  const { canUploadDocuments, isReadOnly } = useSubscription()

  // Reachable by URL even with the upload button disabled. Leaves the way it came, so
  // an errand-scoped upload returns to its errand rather than dumping the user in
  // /documents.
  useEffect(() => {
    if (!isReadOnly) return
    if (errandId) {
      navigate({ params: { id: errandId }, replace: true, to: '/errands/$id' })
      return
    }
    navigate({ replace: true, to: '/documents' })
  }, [isReadOnly, errandId, navigate])

  const createDocuments = useCreateDocuments()
  const deleteDocument = useDeleteDocument()
  const discardDrafts = useDiscardDrafts()
  const commitDocument = useCommitDocument()

  // fileId → AbortController for in-flight PUT cancellation. Held in a ref so
  // the imperative .abort() paths get stable access without re-renders.
  const abortControllers = useRef(new Map<string, AbortController>())
  // Mirror of which fileIds are currently uploading, in state so the UI rerenders.
  const [uploadingIds, markUploading, markFinished] = useStringSet()
  // FileIds whose client-side PUT has completed. The backend trigger flips
  // file.uploaded eventually, but we track this locally so the UI doesn't show
  // "väntar" between the PUT finishing and the trigger firing.
  const [locallyUploadedIds, markLocallyUploaded] = useStringSet()

  const [isCommitting, setIsCommitting] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  // codes of the row whose warning info dialog is open; null = closed
  const [infoCodes, setInfoCodes] = useState<string[] | null>(null)
  // All drafts for this individual (independent of file count / combined-ness).
  // No tombstone set is needed: the delete mutation removes the row from the
  // cache and cancels in-flight individual fetches, so nothing can paint it back.
  const allDrafts = useMemo<DraftDocument[]>(
    () =>
      (selectedIndividual?.documents ?? [])
        .filter(d => d.status === 'draft')
        .map(d => ({
          files: (d.files ?? []) as DocumentFile[],
          id: d.id,
          name: d.name,
          preChecks: d.preChecks,
          status: d.status,
        })),
    [selectedIndividual?.documents],
  )

  // Combined means one thing: more than one file. This page only ever creates
  // single-file drafts, so nothing it produces can be combined — the notice
  // below is purely a pointer back to a multi-file draft left in the wizard.
  //
  // It deliberately does NOT compare the document name against the file's:
  // `name` is trimmed on its way out of the API (it is the display name) while
  // `originalName` is kept verbatim, so dropping " KAROLINSKA, scannat.pdf"
  // made a plain upload look renamed, and the wizard notice appeared for a
  // document this page had just created itself.
  //
  // A ZERO-file draft is neither combined nor ready: the Document is written
  // before its files[] is attached, and every finished upload invalidates the
  // individual query, so a refetch landing in that window sees a file-less
  // draft. It is skipped by both sets — nothing to render, nothing to commit —
  // and joins `drafts` as soon as its files land.
  const drafts = useMemo(() => allDrafts.filter(d => d.files.length === 1), [allDrafts])

  useDraftDocumentsLiveSync(
    selectedIndividualId ?? undefined,
    drafts.map(d => d.id),
  )

  const preChecksPending = useMemo(
    () => drafts.some(d => !d.preChecks?.status || d.preChecks.status === 'pending'),
    [drafts],
  )

  const startFileUpload = useCallback(
    async (fileId: string, filePath: string, file: File) => {
      const controller = new AbortController()
      abortControllers.current.set(fileId, controller)
      markUploading(fileId)

      try {
        await uploadToStoragePath(filePath, file, controller.signal)
        // Optimistic: client-side upload finished. The backend trigger will
        // eventually set file.uploaded=true, but we don't want the UI to flash
        // "väntar" in the meantime.
        markLocallyUploaded(fileId)
        // Kick a refetch so backend state (uploadStatus = 'uploaded') reconciles
        // with our optimistic local state once the trigger fires.
        queryClient.invalidateQueries({
          queryKey: [useIndividualsServiceGetV1IndividualsByIndividualIdKey],
        })
      } catch (err) {
        if ((err as Error).name === 'AbortError') return
        console.error('Upload failed:', err)
        toast.error(t('uploadPage.uploadFailed', { name: file.name }))
      } finally {
        abortControllers.current.delete(fileId)
        markFinished(fileId)
      }
    },
    [t, queryClient, markUploading, markLocallyUploaded, markFinished],
  )

  const handleDropForNewDocuments = useCallback(
    async (acceptedFiles: File[]) => {
      if (!selectedIndividualId) {
        toast.error(t('uploadPage.noIndividual'))

        return
      }

      try {
        // Create one draft Document per dropped file (single-file draft each).
        // The user combines them later via the dialog.
        const response = await createDocuments.mutateAsync({
          individualId: selectedIndividualId,
          requestBody: acceptedFiles.map(file => ({
            errand: errandId,
            files: [{ bytes: file.size, mimeType: file.type, originalName: file.name }],
            name: file.name,
          })),
        })
        // Start parallel uploads
        await Promise.all(
          response.map((doc, i) => {
            const file = acceptedFiles[i]
            const fileInfo = doc.files[0]
            if (!file || !fileInfo) return Promise.resolve()

            return startFileUpload(fileInfo.fileId, fileInfo.filePath, file)
          }),
        )
      } catch (err) {
        console.error('Create drafts failed:', err)
        toast.error(t('uploadPage.createFailed'))
      }
    },
    [selectedIndividualId, createDocuments, startFileUpload, t],
  )

  const handleDeleteDocument = useCallback(
    async (doc: DraftDocument) => {
      if (!selectedIndividualId) return

      for (const f of doc.files) {
        if (f.id) abortControllers.current.get(f.id)?.abort()
      }

      // The row is gone from the cache before this resolves (see
      // `useDeleteDocument`), so rapid deletes don't queue behind each other's
      // refetch. A genuine failure rolls the row back and toasts.
      try {
        await deleteDocument.mutateAsync({
          documentId: doc.id,
          individualId: selectedIndividualId,
        })
      } catch (err) {
        console.error('Delete failed:', err)
        toast.error(t('uploadPage.deleteFailed'))
      }
    },
    [selectedIndividualId, deleteDocument, t],
  )

  const handleDiscardAll = useCallback(async () => {
    if (!selectedIndividualId) return
    if (drafts.length === 0) return
    if (!window.confirm(t('uploadPage.discardAllConfirm'))) return

    // Abort all in-flight uploads first — the blobs won't matter once the
    // Documents are deleted, but we don't want background PUTs leaking past.
    for (const controller of abortControllers.current.values()) controller.abort()

    abortControllers.current.clear()

    // Decide where to go BEFORE the mutation so we read the pre-discard state.
    // If the individual has other (non-draft) Documents we route to /documents
    // to show them. If not, stay on /documents/upload — its own empty-state
    // dropzone will render once the refetch lands. Navigating to /documents
    // when there's nothing to show causes a bounce (zero docs → redirect back
    // to /documents/upload), which the user sees as a flash.
    const hasOtherDocs = (selectedIndividual?.documents ?? []).some(d => d.status !== 'draft')

    await discardDrafts.mutateAsync({ individualId: selectedIndividualId })

    if (errandId) {
      navigate({ ignoreBlocker: true, params: { id: errandId }, to: '/errands/$id' })
    } else if (hasOtherDocs) {
      navigate({ ignoreBlocker: true, to: '/documents' })
    }
  }, [
    drafts.length,
    selectedIndividual?.documents,
    selectedIndividualId,
    errandId,
    discardDrafts,
    navigate,
    t,
  ])

  const performCommit = useCallback(async () => {
    if (!selectedIndividualId) return
    setIsCommitting(true)

    try {
      for (const doc of drafts) {
        await commitDocument.mutateAsync({
          documentId: doc.id,
          individualId: selectedIndividualId,
        })
      }

      if (errandId) {
        // Chat shows a loading bubble until the backend's automated reaction lands — see
        // `pendingErrandReactionId` and `useErrandReactionRealtime`.
        dispatch(AssistantActions.setPendingErrandReaction(errandId))
        navigate({ ignoreBlocker: true, params: { id: errandId }, to: '/errands/$id' })
      } else {
        navigate({ ignoreBlocker: true, to: '/documents' })
      }
    } catch (err) {
      console.error('Commit failed:', err)
      toast.error(t('uploadPage.commitFailed'))
    } finally {
      setIsCommitting(false)
    }
  }, [drafts, selectedIndividualId, errandId, commitDocument, navigate, t, dispatch])

  const handleCommitAll = useCallback(async () => {
    if (!selectedIndividualId) return
    if (drafts.length === 0) return

    if (uploadingIds.size > 0) {
      toast.error(t('uploadPage.stillUploading'))

      return
    }

    const hasWarnings = drafts.some(d => d.preChecks?.status === 'warning')

    if (hasWarnings) {
      setConfirmOpen(true)

      return
    }

    await performCommit()
  }, [drafts, selectedIndividualId, uploadingIds, t, performCommit])

  const dropzone = useDropzone({
    accept: appConfig.documentUpload.allowedFileTypes,
    maxSize: appConfig.documentUpload.maxUploadSize,
    multiple: true,
    // The whole page is wrapped in the dropzone root so drag-and-drop works
    // anywhere — but click-to-open must stay off, otherwise every X / button /
    // input click bubbles up and opens the file picker. Explicit triggers
    // (the empty-state CTA and the "Add more files" button) call dropzone.open() directly.
    noClick: true,
    noKeyboard: true,
    onDrop: accepted => handleDropForNewDocuments(accepted),
    onDropRejected: (rejections: FileRejection[]) => {
      toast.error(
        t('uploadPage.fileRejected', {
          mb: appConfig.documentUpload.maxUploadSize / (1024 * 1024),
        }),
      )
      console.log('fileRejections', rejections)
    },
  })

  return (
    <>
      <div {...dropzone.getRootProps()} className="space-y-6 z-10">
        <input className="hidden pointer-events-none" {...dropzone.getInputProps()} />
        {errandId && (
          <Link
            className="text-sm text-text-secondary hover:underline"
            params={{ id: errandId }}
            to="/errands/$id"
          >
            {t('uploadPage.backToErrand')}
          </Link>
        )}
        <PageHeader
          body={t('uploadPage.header.description')}
          heading={t('uploadPage.header.title')}
          titleAction={
            <PlayVideoButton
              titleKey={UPLOAD_DOCUMENTS_VIDEO.titleKey}
              youtubeId={UPLOAD_DOCUMENTS_VIDEO.youtubeId}
            />
          }
        />

        <div className="space-y-2">
          <h4 className="text-base font-bold">{t('uploadPage.tab.heading')}</h4>
          <Tabs value="upload">
            <TabsList>
              <TabsTrigger value="upload">{t('uploadPage.tab.upload.label')}</TabsTrigger>
              <TabsTrigger onClick={() => navigate({ to: '/documents/combine' })} value="combine">
                {t('uploadPage.tab.combine.label')}
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {drafts.length === 0 ? (
          <div className="space-y-2">
            <div
              className="flex flex-col items-center justify-center gap-4 p-12 border-2 border-dashed rounded-lg cursor-pointer hover:bg-accent transition-colors"
              onClick={dropzone.open}
            >
              <FileUpIcon className="size-12 text-text-secondary" />
              <p className="text-lg font-medium">{t('uploadPage.dropzone.title')}</p>
              <p className="text-base text-text-secondary max-w-sm text-center">
                {t('uploadPage.dropzone.subtitle')}
              </p>
            </div>
            <p className="text-xs text-text-secondary">{t('uploadPage.dropzone.formats')}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {drafts.map(doc => (
              <DraftRow
                doc={doc}
                isLocallyUploaded={fileId => locallyUploadedIds.has(fileId)}
                isUploading={fileId => uploadingIds.has(fileId)}
                key={doc.id}
                onDeleteDocument={() => handleDeleteDocument(doc)}
                onShowWarning={codes => setInfoCodes(codes)}
              />
            ))}
            <button
              className="w-full p-6 border-2 border-dashed rounded-lg text-text-secondary text-sm hover:bg-accent transition-colors"
              onClick={dropzone.open}
              type="button"
            >
              {t('uploadPage.addMore')}
            </button>
          </div>
        )}

        {drafts.length > 0 && (
          <Alert variant="info">
            <InfoIcon />
            <AlertDescription>{t('uploadPage.processingInfo')}</AlertDescription>
          </Alert>
        )}

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-2 sticky bottom-0 bg-background py-4 flex-wrap">
          <Button
            className="w-full sm:w-auto"
            disabled={
              drafts.length === 0 || isCommitting || preChecksPending || !canUploadDocuments
            }
            onClick={handleCommitAll}
          >
            {isCommitting || preChecksPending ? (
              <Loader2 className="animate-spin" />
            ) : (
              <FileUpIcon />
            )}
            {preChecksPending ? t('preCheckRunning') : t('uploadPage.uploadAndAnalyze')}
          </Button>
        </div>
      </div>

      <DropzoneOverlay
        isDragActive={dropzone.isDragActive}
        message={t('documentsPage.dropzoneMessage')}
      />
      <PreCheckConfirmDialog
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => {
          setConfirmOpen(false)
          performCommit()
        }}
        open={confirmOpen}
      />
      <PreCheckWarningInfoDialog
        codes={infoCodes ?? []}
        onClose={() => setInfoCodes(null)}
        open={infoCodes !== null}
      />
      <LeaveDraftsGuard
        hasPendingDrafts={allDrafts.length > 0}
        onConfirmDiscard={async () => {
          if (!selectedIndividualId) return

          for (const controller of abortControllers.current.values()) controller.abort()

          abortControllers.current.clear()
          await discardDrafts.mutateAsync({ individualId: selectedIndividualId })
        }}
        onUnloadDiscard={() =>
          selectedIndividualId &&
          discardOnUnload(`/v1/individuals/${selectedIndividualId}/documents/drafts`)
        }
      />
    </>
  )
}

// ─── Sub-components ───────────────────────────────────────────────────────────

type DraftRowProps = {
  doc: DraftDocument
  isLocallyUploaded(fileId: string): boolean
  isUploading(fileId: string): boolean
  onDeleteDocument(): void
  onShowWarning(codes: string[]): void
}

const DraftRow = ({
  doc,
  isLocallyUploaded,
  isUploading,
  onDeleteDocument,
  onShowWarning,
}: DraftRowProps) => {
  const { t } = useTranslation()
  const previewDocument = usePreviewDocument()
  const primaryFile = doc.files[0]
  if (!primaryFile) return null

  const canPreview =
    primaryFile.uploaded || (primaryFile.id != null && !isUploading(primaryFile.id))

  const hasWarning = doc.preChecks?.status === 'warning'
  const isUploadingFile =
    primaryFile.id != null && isUploading(primaryFile.id) && !isLocallyUploaded(primaryFile.id)
  const isFileUploaded =
    primaryFile.uploaded || (primaryFile.id != null && isLocallyUploaded(primaryFile.id))
  const preChecksRunning =
    isFileUploaded && (!doc.preChecks?.status || doc.preChecks.status === 'pending')
  const showSpinner = isUploadingFile || preChecksRunning

  return (
    <div className="flex items-center gap-3 p-3 border rounded-lg bg-card transition-colors hover:bg-layer-2">
      <span aria-hidden className="w-5 shrink-0" />
      <FileTypeIcon
        className="size-5 text-text-secondary shrink-0"
        mimeType={primaryFile.mimeType}
      />
      <span className="flex-1 truncate text-sm">{primaryFile.originalName}</span>
      {hasWarning ? (
        <Button
          className="hover:bg-layer-1 shrink-0 text-destructive hover:text-destructive gap-1.5 px-2"
          onClick={() => onShowWarning(doc.preChecks?.results?.warnings ?? [])}
          size="sm"
          variant="ghost"
        >
          <AlertTriangleIcon className="size-4 shrink-0" />
          <span className="text-xs">
            {(doc.preChecks?.results?.warnings ?? [])
              .map(code => {
                if (code === 'missing_person') return t('uploadPage.preCheck.missing_person.short')
                if (code === 'missing_pages') return t('uploadPage.preCheck.missing_pages.short')

                return code
              })
              .join(', ')}
          </span>
        </Button>
      ) : showSpinner ? (
        <div className="size-9 flex items-center justify-center shrink-0">
          <Loader2 className="size-4 animate-spin text-text-secondary" />
        </div>
      ) : (
        <div className="size-9 shrink-0" />
      )}
      <div className="flex items-center gap-2">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              aria-label={t('documentsTable.actions.openDocument')}
              className="hover:bg-layer-1"
              // Gate preview on the backend truth (uploaded === converted), not the
              // optimistic local flag — otherwise a HEIC previews before the
              // storage trigger swaps it to JPEG.
              disabled={!primaryFile.uploaded}
              onClick={() => previewDocument(primaryFile.filePath, primaryFile.mimeType)}
              size="icon"
              variant="ghost"
            >
              <FileIcon />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t('documentsTable.actions.openDocument')}</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              aria-label={t('uploadPage.delete')}
              className="hover:bg-layer-1"
              onClick={onDeleteDocument}
              size="icon"
              variant="ghost"
            >
              <XIcon />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t('uploadPage.delete')}</TooltipContent>
        </Tooltip>
      </div>
    </div>
  )
}
